import { useState } from "react";
import { Building2, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";

interface CompanyInfo {
  user_id: string;
  company_id: string;
  company_name: string;
  company_code: string;
  company_logo_url: string | null;
  role: string;
  user_type: string;
  is_active: boolean;
  last_login_at: string | null;
}

interface CompanySelectorProps {
  companies: CompanyInfo[];
  email: string;
  onSelect: (companyCode: string) => Promise<boolean>;
  onCancel: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  super_admin: "최고관리자",
  admin: "관리자",
  dept_leader: "부서장",
  staff: "직원",
  customer: "고객",
};

const USER_TYPE_LABELS: Record<string, string> = {
  employee: "직원",
  external_staff: "외부직원",
  customer: "고객",
  other: "기타",
};

export default function CompanySelector({
  companies,
  email,
  onSelect,
  onCancel,
}: CompanySelectorProps) {
  const [selectingCode, setSelectingCode] = useState<string | null>(null);

  const handleSelect = async (companyCode: string) => {
    if (selectingCode) return;
    setSelectingCode(companyCode);
    const success = await onSelect(companyCode);
    if (!success) {
      setSelectingCode(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2">기업 선택</h2>
        <p className="text-sm text-muted-foreground">
          {email} 계정으로 로그인할 기업을 선택하세요
        </p>
      </div>

      <div className="space-y-3">
        {companies.map((company) => {
          const isSelecting = selectingCode === company.company_code;
          const isDisabled = selectingCode !== null;

          return (
          <Card
            key={company.company_id}
            className={`transition-colors ${isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-primary"} ${isSelecting ? "border-primary opacity-100" : ""}`}
            onClick={() => handleSelect(company.company_code)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {company.company_logo_url ? (
                    <img
                      src={company.company_logo_url}
                      alt={company.company_name}
                      className="h-10 w-10 rounded object-contain flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <CardTitle className="text-lg">{company.company_name}</CardTitle>
                    <CardDescription className="text-xs mt-1">
                      {company.company_code}
                    </CardDescription>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={isDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(company.company_code);
                  }}
                >
                  {isSelecting ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-1" /> 로그인 중...</>
                  ) : "선택"}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline">
                  {USER_TYPE_LABELS[company.user_type] || company.user_type}
                </Badge>
                <Badge
                  variant={
                    company.role === "admin" || company.role === "super_admin"
                      ? "default"
                      : "secondary"
                  }
                >
                  {ROLE_LABELS[company.role] || company.role}
                </Badge>
                {company.last_login_at && (
                  <span className="text-xs text-muted-foreground">
                    마지막 로그인:{" "}
                    {formatDistanceToNow(new Date(company.last_login_at), {
                      addSuffix: true,
                      locale: ko,
                    })}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        );
        })}
      </div>

      <Button variant="outline" className="w-full" onClick={onCancel} disabled={selectingCode !== null}>
        취소
      </Button>
    </div>
  );
}
